import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import { useLinkCopiedToast } from '../hooks/useLinkCopiedToast';
import { featureLabel } from '../utils/userFeatureAccess';

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
}

function BetaFeatureInvites() {
  const { t } = useTranslation();
  const { copyInviteLink, linkCopiedToast } = useLinkCopiedToast();
  const [catalog, setCatalog] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    label: '',
    features: ['interest_test'],
    max_uses: '',
    expires_in_days: '30'
  });

  const loadData = async () => {
    const [catalogRes, invitesRes] = await Promise.all([
      fetch('/api/feature-access/catalog', { headers: authHeaders() }),
      fetch('/api/feature-invites', { headers: authHeaders() })
    ]);
    const catalogData = await catalogRes.json();
    const invitesData = await invitesRes.json();
    if (!catalogRes.ok) throw new Error(catalogData.message || 'Failed to load catalog');
    if (!invitesRes.ok) throw new Error(invitesData.message || 'Failed to load invites');
    setCatalog(catalogData.features || []);
    setInvites(invitesData.invites || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleFeature = (key) => {
    setForm((prev) => {
      const has = prev.features.includes(key);
      const features = has ? prev.features.filter((f) => f !== key) : [...prev.features, key];
      return { ...prev, features: features.length ? features : [key] };
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const res = await apiClient.post('/api/feature-invites', {
        label: form.label.trim(),
        features: form.features,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_in_days: form.expires_in_days ? Number(form.expires_in_days) : 30
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create invite');
      setMessage(t('featureAccess.inviteCreated'));
      setForm({ label: '', features: ['interest_test'], max_uses: '', expires_in_days: '30' });
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (inviteId) => {
    setError('');
    try {
      const res = await apiClient.post(`/api/feature-invites/${encodeURIComponent(inviteId)}/revoke`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to revoke invite');
      await loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const copyLink = (url) => copyInviteLink(url, {
    onError: () => setError(t('featureAccess.copyFailed'))
  });

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10">
        <p className="text-ink-muted">{t('featureAccess.loading')}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-2xl font-bold text-ink">{t('featureAccess.adminTitle')}</h1>
      <p className="mt-2 text-sm text-ink-muted">{t('featureAccess.adminDescription')}</p>

      {error ? <p className="mt-4 text-sm text-red-700">{error}</p> : null}
      {message ? <p className="mt-4 text-sm text-green-700">{message}</p> : null}

      <form onSubmit={handleCreate} className="mt-8 rounded-lg border border-edge bg-surface p-6 shadow-sm space-y-4">
        <h2 className="text-lg font-semibold text-ink">{t('featureAccess.createInvite')}</h2>
        <div>
          <label className="block text-sm font-medium text-ink-muted">{t('featureAccess.labelField')}</label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
            className="mt-1 w-full rounded-md border border-edge-strong px-3 py-2 text-sm"
            placeholder={t('featureAccess.labelPlaceholder')}
          />
        </div>
        <fieldset>
          <legend className="text-sm font-medium text-ink-muted">{t('featureAccess.featuresField')}</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {catalog.map((feature) => (
              <label key={feature.key} className="inline-flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={form.features.includes(feature.key)}
                  onChange={() => toggleFeature(feature.key)}
                />
                {featureLabel(t, feature.key)}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-muted">{t('featureAccess.maxUses')}</label>
            <input
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm((prev) => ({ ...prev, max_uses: e.target.value }))}
              className="mt-1 w-full rounded-md border border-edge-strong px-3 py-2 text-sm"
              placeholder={t('featureAccess.unlimited')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-muted">{t('featureAccess.expiresDays')}</label>
            <input
              type="number"
              min="1"
              value={form.expires_in_days}
              onChange={(e) => setForm((prev) => ({ ...prev, expires_in_days: e.target.value }))}
              className="mt-1 w-full rounded-md border border-edge-strong px-3 py-2 text-sm"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-60"
        >
          {submitting ? t('featureAccess.creating') : t('featureAccess.createInvite')}
        </button>
      </form>

      <div className="mt-10">
        <h2 className="text-lg font-semibold text-ink">{t('featureAccess.existingInvites')}</h2>
        <ul className="mt-4 space-y-3">
          {invites.map((invite) => (
            <li key={invite.invite_id} className="rounded-lg border border-edge bg-surface p-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <p className="font-medium text-ink">{invite.label || invite.code}</p>
                  <p className="text-sm text-ink-muted">
                    {(invite.features || []).map((key) => featureLabel(t, key)).join(', ')}
                  </p>
                  <p className="mt-1 text-xs text-ink-subtle break-all">{invite.join_url}</p>
                  <p className="text-xs text-ink-subtle">
                    {invite.active ? t('featureAccess.active') : t('featureAccess.inactive')}
                    {' · '}
                    {t('featureAccess.uses', { count: invite.use_count || 0 })}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => copyLink(invite.join_url)}
                    className="rounded-md border border-edge-strong px-3 py-1.5 text-sm hover:bg-surface-muted"
                  >
                    {t('featureAccess.copyLink')}
                  </button>
                  {invite.active ? (
                    <button
                      type="button"
                      onClick={() => handleRevoke(invite.invite_id)}
                      className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                    >
                      {t('featureAccess.revoke')}
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {linkCopiedToast}
    </div>
  );
}

export default BetaFeatureInvites;
